import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { DispenserService } from '@/features/dispensers/services/DispenserService.ts';
import { GridState } from '@/common/api/sieveAdapter.ts';
import { CursorRequestParams } from '@/common/api/schemas.ts';
import { TimelineDetailRequestParams } from '@/features/dispensers/services/schemas.ts';

export const dispenserQueryKeys = {
  all: ['dispensers'],
  detail: (id: string) => [...dispenserQueryKeys.all, 'detail', id],
  list: (gridState?: GridState) => [...dispenserQueryKeys.all, 'list', ...[gridState]],
  lastEvents: (dispenserId: string) => [...dispenserQueryKeys.all, dispenserId, 'last-events'],
  timeline: (dispenserId: string, params: CursorRequestParams) => [
    ...dispenserQueryKeys.all,
    dispenserId,
    'timeline',
    params,
  ],
  timelineDetail: (dispenserId: string, params: Omit<TimelineDetailRequestParams, 'cursor'>) => [
    ...dispenserQueryKeys.all,
    dispenserId,
    'timeline-detail',
    params,
  ],
};

export const dispenserQueries = {
  detail: (id: string) =>
    queryOptions({
      queryKey: dispenserQueryKeys.detail(id),
      queryFn: () => DispenserService.detail(id),
    }),
  list: (gridState?: GridState) =>
    queryOptions({
      queryKey: dispenserQueryKeys.list(gridState),
      queryFn: () => DispenserService.list(gridState),
    }),
  lastEvents: (dispenserId: string) =>
    queryOptions({
      queryKey: dispenserQueryKeys.lastEvents(dispenserId),
      queryFn: () => DispenserService.lastEvents(dispenserId),
    }),
  timeline: (dispenserId: string, params: CursorRequestParams) =>
    queryOptions({
      queryKey: dispenserQueryKeys.timeline(dispenserId, params),
      queryFn: () => DispenserService.timeline(dispenserId, params),
    }),
  timelineInfinite: (dispenserId: string, params: Omit<CursorRequestParams, 'cursor'>) =>
    infiniteQueryOptions({
      queryKey: dispenserQueryKeys.timeline(dispenserId, params),
      queryFn: ({ pageParam }) =>
        DispenserService.timeline(dispenserId, {
          ...params,
          cursor: pageParam,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.cursor,
    }),
  timelineDetail: (dispenserId: string, params: TimelineDetailRequestParams) =>
    queryOptions({
      queryKey: dispenserQueryKeys.timelineDetail(dispenserId, params),
      queryFn: () => DispenserService.timelineDetail(dispenserId, params),
    }),
  timelineDetailInfinite: (
    dispenserId: string,
    params: Omit<TimelineDetailRequestParams, 'cursor'>
  ) =>
    infiniteQueryOptions({
      queryKey: dispenserQueryKeys.timelineDetail(dispenserId, params),
      queryFn: ({ pageParam }) =>
        DispenserService.timelineDetail(dispenserId, {
          ...params,
          cursor: pageParam,
        }),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (lastPage) => lastPage.cursor,
    }),
};
